// frontend/src/components/ui/Iparc.js
// Shared chrome/ink primitives so every owner page matches the dashboard.
// Colours come from theme/iparc.js — no page should hand-roll slate/blue again.
//
// Usage:
//   <Page title="Mes véhicules" actions={<Button>Ajouter</Button>}>
//     <Panel>…</Panel>
//   </Page>

import { useEffect } from 'react';
import { signal } from '../../theme/iparc';
import { IconChevron } from '../icons/Icons';

/* ── Page shell ─────────────────────────────────────────────────────────────
   Same width/padding rhythm as the dashboard so pages line up when you
   navigate between them. */
export function Page({ title, subtitle, actions, children, width = 'wide' }) {
  const max = width === 'narrow' ? 'max-w-[900px]' : 'max-w-[1420px]';
  return (
    <div className="text-[#F4F1EA] font-['IBM_Plex_Sans',system-ui,sans-serif] antialiased">
      <main className={`w-full ${max} px-[30px] pt-[26px] pb-[60px] flex flex-col gap-[18px]`}>
        {(title || actions) && (
          <header className="flex flex-wrap items-end justify-between gap-5">
            <div>
              {subtitle && (
                <p className="text-[11px] uppercase tracking-[.2em] text-[#6E665C] mb-1.5">{subtitle}</p>
              )}
              {title && (
                <h1 className="font-['Space_Grotesk',sans-serif] text-[30px] font-semibold tracking-[-.025em] leading-[1.1]">
                  {title}
                </h1>
              )}
            </div>
            {actions && <div className="flex flex-wrap gap-2.5">{actions}</div>}
          </header>
        )}
        {children}
      </main>
    </div>
  );
}

/* ── Panel ───────────────────────────────────────────────────────────────── */
export function Panel({ title, action, children, className = '', bodyClass = 'p-[18px]' }) {
  return (
    <section
      className={`rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden ${className}`}
    >
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 px-[18px] py-3.5 border-b border-[#241F1B]">
          {title && (
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">{title}</p>
          )}
          {action}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

/* ── Buttons ─────────────────────────────────────────────────────────────── */
const BTN_BASE =
  'h-[34px] px-3.5 rounded-[9px] text-[12.5px] font-medium inline-flex items-center justify-center gap-1.5 ' +
  'transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

const VARIANTS = {
  primary:
    'bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] hover:from-white hover:to-[#DED9CE] text-[#141210] font-semibold',
  ghost:
    'border border-[#2B2724] hover:border-[#4E4740] bg-[#141210] text-[#C9C3B6] hover:text-[#F4F1EA]',
  quiet: 'text-[#8F877B] hover:text-[#F4F1EA]',
  danger:
    'border border-[#3A2622] bg-[#1A1210] text-[#E2574B] hover:border-[#E2574B]/50',
};

export function Button({ variant = 'ghost', className = '', as: As = 'button', ...rest }) {
  return <As className={`${BTN_BASE} ${VARIANTS[variant] || VARIANTS.ghost} ${className}`} {...rest} />;
}

/* ── Status badge ────────────────────────────────────────────────────────────
   tone: ok | warn | danger | neutral — the dashboard's two-signal rule. */
const TONES = {
  ok: { color: signal.ok, tint: signal.okTint },
  warn: { color: signal.warn, tint: signal.warnTint },
  danger: { color: signal.danger, tint: signal.dangerTint },
  neutral: { color: '#8F877B', tint: 'rgba(143,135,123,.12)' },
};

export function Badge({ tone = 'neutral', children, className = '' }) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <span
      className={`inline-block px-2 py-[2px] rounded-full text-[10px] font-semibold uppercase tracking-[.06em] whitespace-nowrap ${className}`}
      style={{ background: t.tint, color: t.color }}
    >
      {children}
    </span>
  );
}

/* ── Table ───────────────────────────────────────────────────────────────────
   Scrolls inside its own container so a wide table never scrolls the page. */
export function Table({ head = [], children, minWidth = 720 }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse" style={{ minWidth }}>
        {head.length > 0 && (
          <thead>
            <tr className="border-b border-[#241F1B]">
              {head.map((h, i) => (
                <th
                  key={i}
                  className="px-[18px] py-3 text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] font-normal whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Tr({ onClick, children, className = '' }) {
  return (
    <tr
      onClick={onClick}
      className={`border-b border-[#1E1B18] last:border-0 ${
        onClick ? 'cursor-pointer hover:bg-[#1A1613]' : ''
      } transition-colors ${className}`}
    >
      {children}
    </tr>
  );
}

export function Td({ children, className = '', mono = false }) {
  return (
    <td
      className={`px-[18px] py-3 text-[12.5px] text-[#C9C3B6] align-middle ${
        mono ? "font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]" : ''
      } ${className}`}
    >
      {children}
    </td>
  );
}

/* ── States ──────────────────────────────────────────────────────────────── */
export function EmptyState({ title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <p className="text-[13px] text-[#8F877B]">{title}</p>
      {hint && <p className="text-[11.5px] text-[#5C554C] max-w-[380px]">{hint}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = 'Chargement…' }) {
  return <p className="py-14 text-center text-[12.5px] text-[#6E665C]">{label}</p>;
}

export function ErrorNote({ children }) {
  if (!children) return null;
  return (
    <div
      className="rounded-xl px-4 py-3 text-[12.5px]"
      style={{ background: signal.dangerTint, color: signal.danger }}
    >
      {children}
    </div>
  );
}

/* ── Back link ───────────────────────────────────────────────────────────── */
export function BackLink({ onClick, children = 'Retour' }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[12.5px] text-[#8F877B] hover:text-[#F4F1EA] transition-colors"
    >
      <IconChevron className="w-3.5 h-3.5 rotate-180" />
      {children}
    </button>
  );
}

/* ── Form field ──────────────────────────────────────────────────────────── */
export const inputClass =
  'w-full h-[38px] px-3 rounded-[9px] border border-[#2B2724] bg-[#100F0D] text-[#F4F1EA] ' +
  "text-[13px] outline-none focus:border-[#4E4740] placeholder:text-[#5C554C] font-['IBM_Plex_Sans',system-ui,sans-serif] " +
  'disabled:opacity-50 disabled:cursor-not-allowed';

// Native <select> needs its own arrow: appearance-none kills the platform one.
// The chevron itself is `.iparc-select` in index.css — a data: URL can't go
// through a Tailwind arbitrary value (spaces and quotes make it drop the class
// silently, leaving a select with no arrow at all).
export const selectClass = `${inputClass} appearance-none pr-8 cursor-pointer iparc-select`;

// Denser select for inline table rows.
export const selectClassSm = `${selectClass} h-[30px] px-2 pr-7 text-[12px] rounded-[7px] iparc-select-sm`;

export const textareaClass =
  'w-full px-3 py-2.5 rounded-[9px] border border-[#2B2724] bg-[#100F0D] text-[#F4F1EA] ' +
  "text-[13px] leading-[1.55] outline-none focus:border-[#4E4740] placeholder:text-[#5C554C] resize-y " +
  "font-['IBM_Plex_Sans',system-ui,sans-serif]";

export function Field({ label, hint, children, className = '', required = false }) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="block text-[11px] text-[#8F877B] mb-1.5">
          {label}
          {required && <span className="text-[#DFA23C] ml-0.5">*</span>}
        </span>
      )}
      {children}
      {hint && <span className="block text-[10.5px] text-[#5C554C] mt-1">{hint}</span>}
    </label>
  );
}

/* ── Checkbox ────────────────────────────────────────────────────────────────
   accent-color keeps it a real <input type=checkbox> (keyboard + a11y intact)
   while matching the chrome palette. */
export function Checkbox({ label, hint, className = '', ...rest }) {
  return (
    <label
      className={`flex items-start gap-2.5 cursor-pointer select-none text-[12.5px] text-[#C9C3B6] ${className}`}
    >
      <input
        type="checkbox"
        className="mt-[2px] w-[15px] h-[15px] shrink-0 rounded-[4px] border border-[#2B2724] bg-[#100F0D] cursor-pointer accent-[#C9C3B6]"
        {...rest}
      />
      <span className="min-w-0">
        {label}
        {hint && <span className="block text-[10.5px] text-[#5C554C] mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

/* ── Inline note ─────────────────────────────────────────────────────────────
   Success / warning counterpart to <ErrorNote>. */
export function Note({ tone = 'ok', children }) {
  if (!children) return null;
  const t = TONES[tone] || TONES.neutral;
  return (
    <div className="rounded-xl px-4 py-3 text-[12.5px]" style={{ background: t.tint, color: t.color }}>
      {children}
    </div>
  );
}

/* ── Modal ───────────────────────────────────────────────────────────────────
   Escape closes, body scroll is locked while open, and a click on the backdrop
   (not the panel) dismisses. */
export function Modal({ open, onClose, title, subtitle, children, footer, width = 'max-w-md' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${width} max-h-[88vh] overflow-y-auto rounded-2xl border border-[#241F1B]
                    bg-gradient-to-b from-[#17140F] to-[#131110] shadow-2xl
                    text-[#F4F1EA] font-['IBM_Plex_Sans',system-ui,sans-serif]`}
      >
        {(title || subtitle) && (
          <header className="px-[18px] pt-[18px] pb-3.5 border-b border-[#241F1B]">
            {subtitle && (
              <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1">{subtitle}</p>
            )}
            {title && (
              <h2 className="font-['Space_Grotesk',sans-serif] text-[17px] font-semibold tracking-[-.015em]">
                {title}
              </h2>
            )}
          </header>
        )}
        <div className="p-[18px] flex flex-col gap-3.5">{children}</div>
        {footer && (
          <footer className="px-[18px] py-3.5 border-t border-[#241F1B] flex justify-end gap-2.5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
